import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { VxToastComponent } from './toast.component';
import { VxToastService } from './toast.service';
import { VxToastEntry, VxToastMenuItem } from './toast.types';
import { toastStackItemVariants } from './toast.variants';

interface StackItem {
  entry: VxToastEntry;
  classes: string;
  /** Behind the front toast: header only (see `VxToastComponent.compact`). */
  compact: boolean;
}

/**
 * Fixed top-right outlet for `VxToastService`. Render it once, in the app
 * shell. The newest toast is on top and up to two older ones peek out
 * beneath it; hovering the stack — or having a toast's menu open — pauses
 * every auto-dismiss timer.
 */
@Component({
  selector: 'vx-toast-stack',
  templateUrl: './toast-stack.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [VxToastComponent],
  host: {
    class: `pointer-events-none fixed inset-x-4 top-4 z-1000 grid font-brand
      sm:inset-x-auto sm:right-4 sm:w-96`,
    '(mouseenter)': 'toasts.pause()',
    '(mouseleave)': 'toasts.resume()',
  },
})
export class VxToastStackComponent {
  protected readonly toasts = inject(VxToastService);

  protected readonly items = computed<StackItem[]>(() =>
    this.toasts.toasts().map((entry, index) => ({
      entry,
      classes: toastStackItemVariants({
        depth: Math.min(index, 2) as 0 | 1 | 2,
      }),
      compact: index > 0,
    }))
  );

  protected runAction(entry: VxToastEntry): void {
    this.toasts.dismiss(entry.id);
    entry.action?.handler();
  }

  protected runMenuItem(entry: VxToastEntry, item: VxToastMenuItem): void {
    // dismiss also releases the menu's pause
    this.toasts.dismiss(entry.id);
    item.handler();
  }
}
